import React, { useState } from "react";
import axios from "axios";
import BoxedTitle from "@/components/BoxedTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: "hire", label: "Hire VBB" },
  { value: "pitch", label: "Pitch a project" },
  { value: "partner", label: "Partner / collaborate" },
  { value: "hi", label: "Just say hi" },
];

const BOX_IMG =
  "https://static.prod-images.emergentagent.com/jobs/063b9062-b1d7-4f16-8d31-e465e039ed51/images/406df7120a6f61d5a26734ee207025f1cc4c38f0ee6cb74ba0fb777e8ec5e47f.png";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    reason: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: "" });
    try {
      const payload = { ...form, reason: form.reason || null };
      await axios.post(`${API}/contact`, payload);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", company: "", reason: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      setStatus({ state: "error", error: String(msg) });
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="vbb-overline">
              <span className="vbb-dot" />
              Contact
            </div>
            <h1
              className="font-display uppercase text-5xl sm:text-7xl lg:text-[8rem] leading-[0.92] tracking-tighter mt-6 pl-[0.35em]"
              data-testid="contact-title"
            >
              <BoxedTitle size="lg" thick>
                Send us
              </BoxedTitle>
              <br />
              <span className="text-[#e34234]">a note.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-[17px] leading-relaxed text-[#2a2a2a]">
              Real messages reach real humans. Tell us about your project,
              your team, your problem — or just say hi. We read everything.
            </p>
          </div>
          <div
            className="lg:col-span-5 aspect-[4/3] border border-[#0a0a0a] overflow-hidden vbb-grain"
            data-testid="contact-hero-image"
          >
            <img
              src={BOX_IMG}
              alt="Floating boxes"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section
        className="vbb-border-t vbb-border-b"
        data-testid="contact-section"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12">
          {/* form */}
          <div className="lg:col-span-8 p-8 lg:p-14 lg:vbb-border-r">
            <form
              onSubmit={onSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[#0a0a0a]"
              data-testid="contact-form"
            >
              <label className="block p-6 vbb-border-b sm:!border-b-0 sm:vbb-border-r">
                <span className="font-mono-tiny text-[#6b6b6b]">Name</span>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={setField("name")}
                  placeholder="Your name"
                  className="mt-3 w-full bg-transparent outline-none text-[16px] text-[#0a0a0a] placeholder:text-[#9a9a9a]"
                  data-testid="contact-name-input"
                />
              </label>
              <label className="block p-6 vbb-border-b">
                <span className="font-mono-tiny text-[#6b6b6b]">Email</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={setField("email")}
                  placeholder="you@domain.com"
                  className="mt-3 w-full bg-transparent outline-none text-[16px] text-[#0a0a0a] placeholder:text-[#9a9a9a]"
                  data-testid="contact-email-input"
                />
              </label>
              <label className="block p-6 vbb-border-b sm:vbb-border-r">
                <span className="font-mono-tiny text-[#6b6b6b]">
                  Company (optional)
                </span>
                <input
                  type="text"
                  value={form.company}
                  onChange={setField("company")}
                  placeholder="Where you’re from"
                  className="mt-3 w-full bg-transparent outline-none text-[16px] text-[#0a0a0a] placeholder:text-[#9a9a9a]"
                  data-testid="contact-company-input"
                />
              </label>
              <label className="block p-6 vbb-border-b relative">
                <span className="font-mono-tiny text-[#6b6b6b]">
                  I’m here to…
                </span>
                <select
                  value={form.reason}
                  onChange={setField("reason")}
                  className="mt-3 w-full bg-transparent outline-none text-[16px] text-[#0a0a0a] appearance-none cursor-pointer"
                  data-testid="contact-reason-select"
                >
                  <option value="">Choose one (optional)</option>
                  {REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <span
                  aria-hidden
                  className="absolute right-6 bottom-7 font-mono-tiny text-[#6b6b6b] pointer-events-none"
                >
                  ▾
                </span>
              </label>
              <label className="block p-6 sm:col-span-2">
                <span className="font-mono-tiny text-[#6b6b6b]">Message</span>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={setField("message")}
                  placeholder="Tell us about the box you’re trying to step out of…"
                  className="mt-3 w-full bg-transparent outline-none text-[16px] text-[#0a0a0a] placeholder:text-[#9a9a9a] resize-none"
                  data-testid="contact-message-input"
                />
              </label>

              <div className="sm:col-span-2 vbb-border-t p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div
                  className="font-mono-tiny text-[#6b6b6b]"
                  aria-live="polite"
                >
                  {status.state === "success" &&
                    "✓ Thanks — we’ll be in touch soon."}
                  {status.state === "error" && (
                    <span className="text-[#e34234]">{status.error}</span>
                  )}
                  {status.state === "idle" && "We respond within a few days."}
                  {status.state === "submitting" && "Sending…"}
                </div>
                <button
                  type="submit"
                  disabled={status.state === "submitting"}
                  className="vbb-btn disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {status.state === "submitting"
                    ? "Sending…"
                    : "Send message"}{" "}
                  <span aria-hidden>→</span>
                </button>
              </div>
            </form>
          </div>

          {/* aside */}
          <aside
            className="lg:col-span-4 p-8 lg:p-14"
            data-testid="contact-aside"
          >
            <div className="font-mono-tiny text-[#6b6b6b]">Direct</div>
            <ul className="mt-4 space-y-3 text-[15px]">
              <li>
                <a
                  href="https://linkedin.com/in/garyebickford"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vbb-link"
                  data-testid="contact-linkedin-link"
                >
                  LinkedIn &#8599;
                </a>
              </li>
              <li>
                <a
                  href="https://allamericandog.club"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vbb-link"
                  data-testid="contact-aadc-link"
                >
                  allamericandog.club &#8599;
                </a>
              </li>
            </ul>

            <div className="vbb-border-t mt-10 pt-8">
              <div className="font-mono-tiny text-[#6b6b6b]">
                What happens next
              </div>
              <ol className="mt-4 space-y-3 text-[15px] leading-relaxed text-[#2a2a2a]">
                <li>
                  <span className="font-mono-tiny mr-2">01</span>A human reads
                  your note.
                </li>
                <li>
                  <span className="font-mono-tiny mr-2">02</span>If it’s a
                  fit, we reply with a short call window.
                </li>
                <li>
                  <span className="font-mono-tiny mr-2">03</span>We figure out
                  if there’s a box worth building.
                </li>
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
